import { action } from '@play-co/replicant';
import * as Inventory from '../inventory';
import createActions from '../config/actions/createActions';
import { config, PromoID } from './promo.ruleset';
import { canClaimPromoRewards, isPromoReady } from './promo.getters';
import { CurrencyGrantFeature } from '../analytics/events';
import { Feature } from '../analytics';
import { resetPromoRewards, setPromoSeen } from './promo.modifiers';
import { createReportableError } from '../utils/errors';
import * as Pet from '../pet';
import { promoGroupActions } from './group/promoGroup.actions';

export default createActions({
  /**
   * Called on each time when promo was shown
   */
  promoWasShown: action((state, id: PromoID, api) => {
    const now = api.date.now();

    if (!isPromoReady(state, id, now)) {
      api.reportError(
        createReportableError(
          'warning',
          new Error('Promo not ready to be shown'),
        ),
        { id },
      );
      return;
    }

    setPromoSeen(state, id, now);

    if (config[id].isRepeatable) {
      resetPromoRewards(state, id);
    }
  }),

  /**
   * Disable promo
   */
  finishPromo: action((state, id: PromoID, api) => {
    state.promo[id] = {
      shownAt: api.date.now(),
      active: false,
      count: state.promo[id]?.count ?? 0,
      claimedRewards: state.promo[id]?.claimedRewards ?? false,
    };
  }),

  /**
   * Reset promo
   */
  resetPromo: action((state, id: PromoID, api) => {
    delete state.promo[id];
  }),

  /**
   * Claim rewards for promo
   */
  claimPromoRewards: action(
    (state, args: { id: PromoID; analytics?: CurrencyGrantFeature }, api) => {
      // Cannot claim rewards.
      const { id } = args;
      if (!canClaimPromoRewards(state, id, api.date.now())) {
        const err = new Error('Cannot claim rewards for promo');
        console.error(err.message, id);
        api.reportError(err, { id });
        return null;
      }

      const now = api.date.now();

      const { isRepeatable = false } = config[id];
      const rewards = config[id].getRewards!(state, api.date.now())!;
      const { gems, makePetHappy } = rewards;

      // Give gems if rewards include gems.
      if (gems) {
        Inventory.modifiers.addFreeGems(
          state,
          gems,
          args.analytics ?? {
            feature: Feature.PROMO._,
            $subFeature: id,
          },
          api,
        );
      }

      // Make pet happy if rewards include makePetHappy.
      if (makePetHappy) {
        Pet.modifiers.makePetHappy(state, now);
      }

      // Mark promo rewards as claimed.
      const promo = state.promo[id];
      const count = promo?.count ?? 1;
      state.promo[id] = {
        shownAt: promo?.shownAt ?? api.date.now(),
        count,
        active: isRepeatable,
        claimedRewards: true,
      };

      return rewards;
    },
  ),

  ...promoGroupActions,
});
